import { useState } from 'react';
import { Upload, FileText, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import type { MatchedAnswer } from '@/lib/api/fddQa';
import { matchMeetingNotes } from '@/lib/api/fddQa';

interface MeetingNotesDialogProps {
  projectId: string;
  disabled: boolean;
  onApply: (match: MatchedAnswer) => Promise<void>;
}

export function MeetingNotesDialog({ projectId, disabled, onApply }: MeetingNotesDialogProps) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [matchResults, setMatchResults] = useState<MatchedAnswer[]>([]);
  const [step, setStep] = useState<'upload' | 'review'>('upload');

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.size <= 50000) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setNotes(String(event.target?.result ?? '').slice(0, 50000));
      };
      reader.readAsText(file);
    } else if (file) {
      toast({ title: 'Please upload notes smaller than 50 KB', variant: 'destructive' });
    }
  };

  const processNotes = async () => {
    if (!notes.trim()) return;

    setIsProcessing(true);

    try {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      const matches = await matchMeetingNotes(projectId, notes);

      setMatchResults(matches);
      setStep('review');

      if (matches.length === 0) {
        toast({
          title: 'No matches found',
          description: 'The AI could not find relevant answers in the meeting notes. Try adding more context.',
        });
      }
    } catch {
      toast({
        title: 'Matching failed',
        description: 'Could not process meeting notes. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const applyMatches = async () => {
    setIsProcessing(true);
    try {
      for (const match of matchResults) {
        await onApply(match);
        setMatchResults((previous) => previous.filter((item) => item.question_id !== match.question_id));
      }
      toast({ title: 'Answers saved' });
      handleClose();
    } catch (error) {
      toast({ title: 'Could not save all answers', description: String(error), variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setStep('upload');
    setNotes('');
    setMatchResults([]);
  };

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!isProcessing) { if (value) setOpen(true); else handleClose(); } }}>
      <DialogTrigger asChild>
        <button type="button" disabled={disabled} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #E5E4E2', background: '#fff', color: '#555', fontSize: 12.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' as const, boxShadow: '0 1px 2px rgba(0,0,0,0.03)', flex: '1 1 0', justifyContent: 'center', fontFamily: 'inherit' }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="1" width="14" height="14" rx="2" fill="#2B579A"/><text x="8" y="11.5" textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff">W</text></svg>
          Upload Meeting Notes
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {step === 'upload' ? 'Upload AI Meeting Notes' : 'Review Matched Answers'}
          </DialogTitle>
          <DialogDescription>
            {step === 'upload'
              ? 'Paste or upload meeting notes and AI will match answers to your questions automatically.'
              : `Found ${matchResults.length} potential answer(s). Review and apply.`
            }
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' ? (
          <div className="space-y-4 flex-1">
            {/* File upload first */}
            <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
              <input
                type="file"
                accept=".txt,.md"
                onChange={handleFileUpload}
                className="hidden"
                id="notes-upload"
              />
              <label htmlFor="notes-upload" className="cursor-pointer">
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  Click to upload
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  TXT or Markdown files
                </p>
              </label>
            </div>

            <div className="text-center text-xs text-muted-foreground">or paste notes below</div>

            {/* Text area */}
            <Textarea
              aria-label="Meeting notes"
              maxLength={50000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Paste your AI meeting notes here...

Example:
- Discussed the spike in Professional Fees for June. CFO confirmed this was due to a one-time legal settlement of $600K.
- Marketing spend increase in September was for the product launch campaign, expected to normalize next quarter.
- A/R increase attributed to a large enterprise deal with extended payment terms (Net 90)."
              className="min-h-[200px] resize-none text-sm"
            />

            <div className="flex justify-end gap-2">
              <Button variant="ghost" disabled={isProcessing} onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={processNotes} disabled={!notes.trim() || isProcessing} className="gap-2">
                <Sparkles className="w-4 h-4" />
                {isProcessing ? 'Analyzing...' : 'Match with AI'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 flex-1 overflow-auto">
            {matchResults.length > 0 ? (
              <div className="space-y-3">
                {matchResults.map((match, idx) => (
                  <div key={match.question_id} className="p-3 rounded-lg border border-border bg-secondary/30">
                    <p className="text-xs text-muted-foreground mb-1">Question {idx + 1}</p>
                    <p className="text-sm font-medium text-foreground mb-2 line-clamp-2">
                      {match.question}
                    </p>
                    <p className="text-xs text-muted-foreground mb-1">Matched Answer</p>
                    <p className="text-sm text-foreground bg-success-bg/50 p-2 rounded border border-success/20">
                      {match.answer}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No matching answers found</p>
                <p className="text-sm">Try adding more detailed notes</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="ghost" disabled={isProcessing} onClick={() => setStep('upload')}>
                Back
              </Button>
              <Button onClick={() => void applyMatches()} disabled={isProcessing || matchResults.length === 0} className="gap-2">
                Apply {matchResults.length} Answer{matchResults.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
